/* eslint-disable @typescript-eslint/no-magic-numbers */
import ct from 'countries-and-timezones';
import {
  format,
  distanceInWordsToNow,
  distanceInWordsStrict,
  isValid
} from 'date-fns';
import en from 'date-fns/locale/en';
import es from 'date-fns/locale/es';
import fr from 'date-fns/locale/fr';
import pt from 'date-fns/locale/pt';
import moment from 'moment';

const defaultFormatDate = 'DD/MM/YYYY HH:mm';

export const shortYearFormatDate = 'DD/MM/YY';
export const shortFormatDate = 'DD/MM/YYYY';

const isDayOrMonthInvalid = (month, day) =>
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  month > 11 || day > 31 || (month === 1 && day > 28);

export const Languages = {
  AR: 'ar',
  DE: 'de',
  EN: 'en',
  ES: 'es',
  FR: 'fr',
  IT: 'it',
  JA: 'ja',
  PT_BR: 'pt-BR'
};

const LanguageFormats = {
  [shortFormatDate]: {
    [Languages.AR]: 'DD/MM/YYYY',
    [Languages.DE]: 'DD/MM/YYYY',
    [Languages.EN]: 'MM/DD/YYYY',
    [Languages.ES]: 'DD/MM/YYYY',
    [Languages.FR]: 'DD/MM/YYYY',
    [Languages.IT]: 'DD/MM/YYYY',
    [Languages.JA]: 'YYYY/MM/DD',
    [Languages.PT_BR]: 'DD/MM/YYYY'
  },
  [defaultFormatDate]: {
    [Languages.AR]: 'DD/MM/YYYY HH:mm',
    [Languages.DE]: 'DD/MM/YYYY HH:mm',
    [Languages.EN]: 'MM/DD/YYYY HH:mm',
    [Languages.ES]: 'DD/MM/YYYY HH:mm',
    [Languages.FR]: 'DD/MM/YYYY HH:mm',
    [Languages.IT]: 'DD/MM/YYYY HH:mm',
    [Languages.JA]: 'YYYY/MM/DD HH:mm',
    [Languages.PT_BR]: 'DD/MM/YYYY HH:mm'
  },
  [shortYearFormatDate]: {
    [Languages.AR]: 'DD/MM/YY',
    [Languages.DE]: 'DD/MM/YY',
    [Languages.EN]: 'MM/DD/YY',
    [Languages.ES]: 'DD/MM/YY',
    [Languages.FR]: 'DD/MM/YY',
    [Languages.IT]: 'DD/MM/YY',
    [Languages.JA]: 'YY/MM/DD',
    [Languages.PT_BR]: 'DD/MM/YY'
  }
};

class DateHelper {
  language = Languages.PT_BR;

  static secondsToHms(day, renderHour = false) {
    day = Number(day);

    if (Number.isNaN(day)) {
      return 0;
    }

    const hours = Math.floor(day / 3600);
    const minutes = Math.floor((day % 3600) / 60);
    const seconds = Math.floor((day % 3600) % 60);

    const sFormat = `0${seconds}`.slice(-2);
    const mFormat = `0${minutes}`.slice(-2);

    let time = `${mFormat}:${sFormat}`;

    if (renderHour) {
      const hFormat = `0${hours}`.slice(-2);

      time = `${hFormat}:${time}`;
    }

    return time;
  }

  static getDateFromString(date) {
    if (!date) {
      return null;
    }

    const isEnglish = this.language === Languages.EN;
    const maxDateLength = 8;
    const year = date.substring(4, 8);
    const month = isEnglish
      ? Number(date.substring(0, 2)) - 1
      : Number(date.substring(2, 4)) - 1;
    const day = isEnglish ? date.substring(2, 4) : date.substring(0, 2);

    if (isDayOrMonthInvalid(month, day) || date.length !== maxDateLength) {
      const invalidDate = new Date('');

      return invalidDate;
    }

    return new Date(year, month, day);
  }

  static getIsoDateByString(date) {
    return this.getDateFromString(date).toISOString();
  }

  static dateFormattedToDateObject(dateFormatted) {
    return this.getDateFromString(dateFormatted.replace(/\//g, ''));
  }

  static formatDate(value, formatDateStr = defaultFormatDate, options) {
    return value ? this.format(new Date(value), formatDateStr, options) : '';
  }

  static isValid(date) {
    return isValid(date);
  }

  static format(date, stringFormat, options) {
    const languageFormats = LanguageFormats[stringFormat];
    const languageFormat = languageFormats && languageFormats[this.language];

    return format(date, languageFormat || stringFormat, options);
  }

  static getShortFormatDate() {
    return LanguageFormats[shortFormatDate][this.language];
  }

  static get locales() {
    return {
      en,
      es,
      fr,
      pt
    };
  }

  static distanceInWordsStrict(firstDate, secondDate, options) {
    return distanceInWordsStrict(firstDate, secondDate, options);
  }

  static distanceInWordsToNow(date, options) {
    return distanceInWordsToNow(date, options);
  }

  static unixTimeToISOString(unixTimeMs) {
    const utcZeroDate = new Date(unixTimeMs);
    const timezoneDate = new Date(
      utcZeroDate.getFullYear(),
      utcZeroDate.getMonth(),
      utcZeroDate.getDate()
    );

    return timezoneDate.toISOString();
  }

  static setMomentLocale() {
    moment.locale(DateHelper.language);
  }

  static getCurrentTimezone() {
    return Intl?.DateTimeFormat()?.resolvedOptions?.()?.timeZone || '';
  }

  static getCountryByTimezone(timezoneName) {
    const timezoneList = ct.getAllTimezones();
    const timezone = timezoneList[timezoneName];

    return timezone?.countries?.[0] || '';
  }

  static removeTimezone(date) {
    const year = date.getFullYear();
    const month = `0${date.getMonth() + 1}`.slice(-2);
    const day = `0${date.getDate()}`.slice(-2);

    const dateISOString = `${year}-${month}-${day}T00:00:00.000Z`;

    return new Date(dateISOString);
  }
}

export default DateHelper;
